import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Video, Building, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, isToday } from 'date-fns';

export function MeetingCalendar({ meetings, onDateClick, onMeetingClick }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const days = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start, end });
    
    // Add padding days from previous month
    const startDayOfWeek = start.getDay();
    const paddingDays = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(start);
      date.setDate(date.getDate() - i - 1);
      paddingDays.push({ date, isCurrentMonth: false });
    }
    
    // Add padding days for next month
    const endDayOfWeek = end.getDay();
    const endPaddingDays = [];
    for (let i = 1; i < 7 - endDayOfWeek; i++) {
      const date = new Date(end);
      date.setDate(date.getDate() + i);
      endPaddingDays.push({ date, isCurrentMonth: false });
    }
    
    return [
      ...paddingDays,
      ...daysInMonth.map(date => ({ date, isCurrentMonth: true })),
      ...endPaddingDays
    ];
  }, [currentMonth]);
  
  const getMeetingsForDate = (date) => {
    return meetings.filter(meeting => {
      try {
        const meetingDate = parseISO(meeting.date);
        return isSameDay(meetingDate, date);
      } catch {
        return false;
      }
    });
  };
  
  const getLocationIcon = (meeting) => {
    if (meeting.location_type === 'online') return <Video className="h-3 w-3" />;
    if (meeting.location_type === 'physical') return <Building className="h-3 w-3" />;
    return <MapPin className="h-3 w-3" />;
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-gray-400';
      default: return 'bg-yellow-500';
    }
  };
  
  return (
    <Card className="w-full" data-testid="meeting-calendar">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            data-testid="prev-month-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            data-testid="next-month-btn"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map(({ date, isCurrentMonth }, index) => {
            const dayMeetings = getMeetingsForDate(date);
            const hasUpcoming = dayMeetings.some(m => m.status !== 'completed');
            
            return (
              <div
                key={index}
                className={`
                  min-h-[80px] p-1 rounded-lg border cursor-pointer transition-colors
                  ${isCurrentMonth ? 'bg-card' : 'bg-muted/30'}
                  ${isToday(date) ? 'border-primary border-2' : 'border-border'}
                  ${hasUpcoming ? 'bg-primary/5' : ''}
                  hover:bg-muted/50
                `}
                onClick={() => onDateClick?.(date, dayMeetings)}
                data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
              >
                <div className={`
                  text-sm font-medium mb-1
                  ${!isCurrentMonth ? 'text-muted-foreground/50' : ''}
                  ${isToday(date) ? 'text-primary' : ''}
                `}>
                  {format(date, 'd')}
                </div>
                
                {/* Meeting indicators */}
                <div className="space-y-1">
                  {dayMeetings.slice(0, 2).map((meeting) => (
                    <div
                      key={meeting.id}
                      className={`
                        text-xs p-1 rounded truncate cursor-pointer
                        ${meeting.status === 'active' ? 'bg-green-500/20 text-green-700 dark:text-green-400' : 
                          meeting.status === 'completed' ? 'bg-gray-500/20 text-gray-600 dark:text-gray-400' :
                          'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'}
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMeetingClick?.(meeting);
                      }}
                      title={meeting.title}
                    >
                      <div className="flex items-center gap-1">
                        {getLocationIcon(meeting)}
                        <span className="truncate">{meeting.title}</span>
                      </div>
                    </div>
                  ))}
                  {dayMeetings.length > 2 && (
                    <div className="text-xs text-muted-foreground text-center">
                      +{dayMeetings.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/50" />
            <span>Active</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/50" />
            <span>Draft</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-500/20 border border-gray-500/50" />
            <span>Completed</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
